import React from 'react';
import { Stack } from '@atlaskit/primitives/compiled';
import { ErrorMessage, Field, HelperMessage } from '@atlaskit/form';
import Textfield from '@atlaskit/textfield';
import { CheckboxSelect, type ValueType } from '@atlaskit/select';
import CopyButton from '../copy-button/index.tsx';
import type { SelectOption } from '../../../common/types.tsx';
import { validateSupportedScopes, validateURLField } from '../../../common/form-utils.tsx';

export const CopyableApplicationClientID = (props: { name: string; clientId: string }) => (
	<Field
		aria-required
		name={props.name}
		defaultValue={props.clientId}
		label="Application Client ID"
		isDisabled
	>
		{({ fieldProps }) => (
			<Textfield
				isReadOnly
				type="password"
				{...fieldProps}
				elemAfterInput={<CopyButton label="Application Client ID" value={props.clientId} />}
			/>
		)}
	</Field>
);

export const CopyableApplicationClientSecret = (props: { name: string; clientSecret: string }) => (
	<Field
		aria-required
		name={props.name}
		defaultValue={props.clientSecret}
		label="Application Client secret"
		isDisabled
	>
		{({ fieldProps }) => (
			<Textfield
				isReadOnly
				type="password"
				{...fieldProps}
				elemAfterInput={<CopyButton label="Application Client secret" value={props.clientSecret} />}
			/>
		)}
	</Field>
);

export const RemoteApplicationScopeMultiSelect = (props: {
	name: string;
	supportedScopes: string[];
	defaultSelected?: ValueType<SelectOption, true>;
}) => (
	<Field<ValueType<SelectOption, true>>
		name={props.name}
		label="Scopes"
		defaultValue={props.defaultSelected}
		validate={(chosenScopes) =>
			validateSupportedScopes(
				chosenScopes ? chosenScopes.map((scope) => scope.value) : [],
				props.supportedScopes,
			)
		}
	>
		{({ fieldProps, error }) => (
			<Stack>
				<CheckboxSelect
					placeholder="Use the same scopes as the remote application"
					{...fieldProps}
					options={props.supportedScopes.map((scope) => ({
						label: scope,
						value: scope,
					}))}
				/>
				{error && <ErrorMessage>{error}</ErrorMessage>}
			</Stack>
		)}
	</Field>
);

export const ApplicationClientId = (props: { name: string; isRequired?: boolean }) => (
	<Field
		aria-required
		name={props.name}
		label="Application Client ID"
		isRequired={props.isRequired}
	>
		{({ fieldProps }) => <Textfield type="password" placeholder="Client ID" {...fieldProps} />}
	</Field>
);

export const ApplicationClientSecret = (props: { name: string; isRequired?: boolean }) => (
	<Field
		aria-required
		name={props.name}
		label="Application Client secret"
		isRequired={props.isRequired}
	>
		{({ fieldProps }) => <Textfield type="password" placeholder="Client secret" {...fieldProps} />}
	</Field>
);

export const RedirectUrl = (props: { name: string; defaultRedirectUrl?: string }) => (
	<Field
		aria-required
		name={props.name}
		defaultValue={props.defaultRedirectUrl}
		label="Redirect URL"
		validate={(redirectUrl) => validateURLField(redirectUrl, true, true)}
		isRequired
	>
		{({ fieldProps, error }) => (
			<>
				<Textfield placeholder="Redirect URL" {...fieldProps} />
				{error && <ErrorMessage>{error}</ErrorMessage>}
			</>
		)}
	</Field>
);

export const DisplayName = (props: { name: string; defaultName: string }) => (
	<Field
		aria-required
		name={props.name}
		defaultValue={props.defaultName}
		label="Display name"
		isRequired
	>
		{({ fieldProps }) => (
			<Stack>
				<Textfield placeholder="Name for the application link" {...fieldProps} />
				<HelperMessage>
					Enter a unique name for the link, for example the name of your external application.
				</HelperMessage>
			</Stack>
		)}
	</Field>
);

export const DisplayUrl = (props: { name: string; defaultUrl: string }) => (
	<Field
		aria-required
		name={props.name}
		defaultValue={props.defaultUrl}
		label="Display URL"
		isRequired
		validate={(defaultUrl) => validateURLField(defaultUrl, true, false)}
	>
		{({ fieldProps, error }) => (
			<Stack>
				<Textfield placeholder="URL for the application link" {...fieldProps} />
				{!error && (
					<HelperMessage>This is the URL that will be displayed to users in Jira.</HelperMessage>
				)}
				{error && <ErrorMessage>{error}</ErrorMessage>}
			</Stack>
		)}
	</Field>
);

export const ApplicationUrl = (props: { name: string; defaultUrl: string; disabled: boolean }) => (
	<Field
		aria-required
		name={props.name}
		defaultValue={props.defaultUrl}
		label="Application URL"
		isDisabled={props.disabled}
		isRequired={!props.disabled}
		validate={(redirectUrl) => validateURLField(redirectUrl, true, true)}
	>
		{({ fieldProps, error }) => (
			<Stack>
				<Textfield placeholder="Application URL for the application link" {...fieldProps} />
				{!error && (
					<HelperMessage>
						This is the URL that Jira will use to communicate with the external application.
					</HelperMessage>
				)}
				{error && <ErrorMessage>{error}</ErrorMessage>}
			</Stack>
		)}
	</Field>
);
